import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { ROUTES } from "src/consts/routes";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { useNavigate, useOutletContext } from "react-router-dom";
import FormContainer from "src/components/Displays/FormContainer";
import BatterySetupForm from "src/components/Forms/BatterySetupForm";
import { batterySetupFormSchema } from "src/schema/batterySetupFormSchema";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import { GlobalContextProps } from "src/components/GlobalContext/GlobalContext";
import { calculateYearlyGridSupply } from "src/helpers/calculateYearlyGridSupply";
import {
  SESSION_KEYS,
  SITE_TECHNICAL_DATA,
  USER_PROFILE_DATA,
} from "src/consts/session";

export enum BatterySetupTypes {
  JA = "true",
  NEIN = "false",
  BLANK = "",
}

export interface BatterySetupFormValues {
  batteryInstalled?: BatterySetupTypes;
  batteryCapacity?: string;
}

const BatterySetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formTitle = t("page.batterySetup.formTitle");
  const { setAnimations } = useOutletContext<GlobalContextProps>();

  const [getSiteTechnicalData, setSiteTechnicalData] =
    useSessionStorage<SITE_TECHNICAL_DATA>(SESSION_KEYS.SITE_TECHNICAL_DATA);
  const [getUserProfileData, setUserProfileData] =
    useSessionStorage<USER_PROFILE_DATA>(SESSION_KEYS.USER_PROFILE_DATA);

  const form = useForm<BatterySetupFormValues>({
    defaultValues: {
      batteryInstalled:
        (getSiteTechnicalData()?.batterySetup
          ?.batteryInstalled as BatterySetupTypes) || BatterySetupTypes.BLANK,
      batteryCapacity: getSiteTechnicalData()?.batterySetup?.batteryCapacity,
    },
    mode: "onSubmit",
    resolver: yupResolver(batterySetupFormSchema),
  });

  const handleBackClick = () => {
    navigate(ROUTES.WALLBOX);
  };

  const getYearlyGridSupplyKwh = (personsInHousehold: number) => {
    const siteTechnicalData = getSiteTechnicalData();
    return calculateYearlyGridSupply(personsInHousehold, siteTechnicalData);
  };

  const onSubmit = (data: BatterySetupFormValues) => {
    const sessionData = getSiteTechnicalData();
    const userProfileData = getUserProfileData();

    const newData = { ...data };
    if (newData.batteryInstalled === BatterySetupTypes.NEIN) {
      if (newData?.batteryCapacity) {
        delete newData.batteryCapacity;
      }
    }
    const updatedSessionData: SITE_TECHNICAL_DATA = {
      ...sessionData,
      batterySetup: newData,
    };
    setSiteTechnicalData(updatedSessionData);

    const updatedUserProfileData: USER_PROFILE_DATA = {
      ...userProfileData,
      yearlyGridSupplyKwh: getYearlyGridSupplyKwh(
        userProfileData?.personsInHousehold ?? 2
      ),
    };
    setUserProfileData(updatedUserProfileData);

    setAnimations((prev) => ({ ...prev, continue: !prev.continue }));
    navigate(ROUTES.ANNUAL_GRID_SUPPLY);
  };

  return (
    <FormContainer title={formTitle} onSubmit={form.handleSubmit(onSubmit)}>
      <Box sx={{ marginTop: 6, marginBottom: 3 }}>
        <BatterySetupForm form={form} />
      </Box>
      <FormSubmitContainer onClick={handleBackClick} />
    </FormContainer>
  );
};

export default BatterySetup;
